<script setup lang="ts">
interface Props {
  tournament: TTournament;
}

const props = defineProps<Props>();

const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const tournamentsStore = useSmarticoTournaments();
const { loadTournamentInfo, getCachedTournamentInfo } = tournamentsStore;
const { tournaments: storeTournaments } = storeToRefs(tournamentsStore);

const tournamentDetails = ref<TTournamentDetailed>();

const tournamentId = computed(
  () => props.tournament.instance_id ?? props.tournament.tournament_id ?? '',
);

const tournamentImageUrl = computed(() => {
  if (import.meta.client && window.innerWidth < 768) {
    return props.tournament.image2_mobile || props.tournament.image2;
  }

  return props.tournament.image2 || props.tournament.image2_mobile;
});

const ribbonText = computed<string | null>(() => {
  const r = String(props.tournament.ribbon || '').trim();

  return r || null;
});

const participantCount = computed(() => {
  return (
    tournamentDetails.value?.registration_count ??
    props.tournament.registration_count ??
    0
  );
});

const formattedParticipantCount = computed(() => {
  try {
    return new Intl.NumberFormat('pt-BR').format(participantCount.value);
  } catch {
    return String(participantCount.value);
  }
});

const prizeAmount = computed(() => {
  if (props.tournament.prize_pool_short) {
    return props.tournament.prize_pool_short;
  }

  return 'Misto';
});

const topPrizes = computed(() => {
  if (
    tournamentDetails.value?.prizes &&
    tournamentDetails.value.prizes.length
  ) {
    return tournamentDetails.value.prizes.slice(0, 3).map((p) => {
      if (p.points !== undefined && p.points !== null) return `R$${p.points}`;
      if (p.name) return p.name;

      return prizeAmount.value;
    });
  }

  return [prizeAmount.value, '-', '-'];
});

function viewTournament() {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  navigateTo(localeLink('/vip/tournaments/' + tournamentId.value));
}

async function loadTournamentDetails(): Promise<boolean> {
  const instanceId = props.tournament.instance_id;

  if (!instanceId) return true;

  // Cache hit: skip the SDK roundtrip entirely (cleared on full reload).
  const cached = getCachedTournamentInfo(instanceId);

  if (cached) {
    tournamentDetails.value = cached;

    return true;
  }

  try {
    // Always go through the store so the result is cached centrally.
    const details = await loadTournamentInfo(instanceId);

    if (details) {
      tournamentDetails.value = details;

      return true;
    }

    return false;
  } catch (error: any) {
    // Smartico SDK throws a null-deref in TournamentItemsTransform when its
    // internal list still has stale/null entries; the data arrives later via
    // the store's onUpdate callback, so we swallow that specific noise.
    const msg = String(error?.message || error || '');
    const isSmarticoNullTournament =
      msg.includes("reading 'tournamentId'") || msg.includes('tournamentId');

    if (!isSmarticoNullTournament) {
      console.error('Error loading tournament details:', error);
    }

    return false;
  }
}

let isAttempting = false;
const hasDetails = computed(() => !!tournamentDetails.value?.prizes?.length);

const tryLoadTournamentDetails = async (
  maxRetries = 12,
  delayMs = 800,
): Promise<void> => {
  if (isAttempting || hasDetails.value) return;
  isAttempting = true;

  try {
    let retries = 0;

    while (retries <= maxRetries) {
      const sdkReady = !!(window && window._smartico);
      const listReady = storeTournaments.value.length > 0;

      if (sdkReady && listReady) {
        const ok = await loadTournamentDetails();

        if (ok) return;
      }

      if (retries === maxRetries) break;
      retries++;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  } finally {
    isAttempting = false;
  }
};

// Whenever the store's tournament list refreshes (Smartico onUpdate callback),
// retry loading details for cards that still don't have prize info. This
// covers cards mounted while the SDK was warming up and exhausted their
// initial retry window.
watch(storeTournaments, () => {
  if (!hasDetails.value) {
    tryLoadTournamentDetails();
  }
});

onMounted(() => {
  tryLoadTournamentDetails();
});
</script>

<template>
  <div
    class="tournament-card"
    @click="viewTournament"
  >
    <!-- Imagem com badges -->
    <div class="tournament-card__image-wrapper">
      <NuxtImg
        :alt="tournament.name"
        class="tournament-card__image"
        :draggable="false"
        :src="tournamentImageUrl"
      />

      <!-- Badge Ribbon (ex: DIÁRIO) -->
      <div
        v-if="ribbonText"
        class="tournament-card__ribbon"
      >
        {{ ribbonText }}
      </div>

      <!-- Badge Participantes -->
      <div class="tournament-card__participants">
        <NuxtIcon
          class="tournament-card__participants-icon"
          name="players"
        />
        <span>{{ formattedParticipantCount }}</span>
      </div>
    </div>

    <!-- Título -->
    <div class="tournament-card__title">
      {{ tournament.name }}
    </div>

    <!-- Top 3 Prêmios -->
    <div class="tournament-card__prizes">
      <div
        v-for="(prize, idx) in topPrizes"
        :key="idx"
        class="tournament-card__prize"
      >
        <NuxtImg
          :alt="`${idx + 1}º lugar`"
          class="tournament-card__prize-icon"
          :src="getAssetsUrlForCDN(`/assets/gamification/place-${idx + 1}.png`)"
        />
        <span class="tournament-card__prize-value">{{ prize }}</span>
      </div>
    </div>

    <!-- Prêmio Total + Botão -->
    <div class="tournament-card__footer">
      <div class="tournament-card__total">
        <span class="tournament-card__total-label">PRÊMIO TOTAL</span>
        <span class="tournament-card__total-value">{{ prizeAmount }}</span>
      </div>
      <button
        class="tournament-card__button"
        @click.stop="viewTournament"
      >
        <span>ENTRAR</span>
        <NuxtIcon name="mission/button/participar" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.tournament-card {
  width: 250px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  cursor: pointer;
  transition: border-color 0.2s;
  user-select: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
}

.tournament-card:hover {
  border-color: #2d3339;
}

/* Imagem */
.tournament-card__image-wrapper {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.tournament-card__image {
  width: 100%;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
}

/* Badge Ribbon */
.tournament-card__ribbon {
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(135deg, #ff704a 0%, #ff3400 100%);
  color: white;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 6px;
  border-bottom-right-radius: 6px;
  text-transform: uppercase;
}

/* Badge Participantes */
.tournament-card__participants {
  position: absolute;
  bottom: 0;
  right: 0;
  background: linear-gradient(135deg, #ff704a 0%, #ff3400 100%);
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 2px 6px;
  border-top-left-radius: 6px;
  display: flex;
  align-items: center;
  gap: 3px;
}

.tournament-card__participants-icon {
  font-size: 9px;
}

/* Título */
.tournament-card__title {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  color: white;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

/* Top 3 Prêmios */
.tournament-card__prizes {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-top: 3px;
  gap: 6px;
}

.tournament-card__prize {
  display: flex;
  align-items: center;
  gap: 2px;
}

.tournament-card__prize-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.tournament-card__prize-value {
  font-size: 8px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 55px;
}

/* Footer: Prêmio Total + Botão */
.tournament-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 5px 6px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.tournament-card__total {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
}

.tournament-card__total-label {
  font-size: 7px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.tournament-card__total-value {
  font-size: 10px;
  font-weight: 800;
  color: #ff9500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Botão */
.tournament-card__button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  background: var(--accent-color, #1414dd);
  border: none;
  border-radius: 4.5px;
  color: white;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.2s;
  flex-shrink: 0;
  white-space: nowrap;
  width: 100%;
  max-width: 144px;
}

.tournament-card__button:hover {
  filter: brightness(1.1);
}
</style>
